import React from 'react';
import './molecules.css';

interface JoinScreenProps {
  projectId?: string;
  projectName?: string;
  organiserName?: string;
  taskCount?: number;
  onContinue: () => void;
  onDecline?: () => void;
  onBack?: () => void;
}

export const JoinScreen: React.FC<JoinScreenProps> = ({
  projectName = 'Consumer Habits Study',
  organiserName = 'Kapil',
  taskCount = 8,
  onContinue,
  onDecline,
  onBack,
}) => {
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#C2AC8E]">
      {/* Molecule decorations */}

      {/* Cluster 1 — Top left, zigzag descending Z pattern */}
      <div className="mol-bloom" style={{position:'absolute',top:50,left:50,pointerEvents:'none'}}>
        <svg width="140" height="130" viewBox="-90 -30 200 150">
          {/* Nodes: Z pattern — top-left, middle-right, bottom-left */}
          <circle cx="0" cy="0" r="5" fill="#fff"/>
          <circle cx="40" cy="45" r="4" fill="#fff"/>
          <circle cx="5" cy="90" r="5" fill="#fff"/>
          {/* White connecting lines */}
          <line x1="0" y1="0" x2="40" y2="45" stroke="#fff" strokeWidth="2"/>
          <line x1="40" y1="45" x2="5" y2="90" stroke="#fff" strokeWidth="2"/>
          {/* 3 waves LEFT from top node */}
          <path d="M-5,0 Q-20,-10 -25,0 Q-30,10 -45,0 Q-50,-10 -65,0 Q-70,10 -85,0" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
          {/* 2 waves RIGHT from middle node */}
          <path d="M44,45 Q58,35 68,45 Q78,55 92,45" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Cluster 2 — Top right, horizontal curve dipping in middle */}
      <div className="mol-bloom-d1" style={{position:'absolute',top:110,right:50,pointerEvents:'none'}}>
        <svg width="140" height="80" viewBox="-10 -30 170 100">
          {/* Nodes: left-high, middle-low, right-high */}
          <circle cx="0" cy="0" r="4" fill="#fff"/>
          <circle cx="50" cy="25" r="5" fill="#fff"/>
          <circle cx="100" cy="0" r="4" fill="#fff"/>
          {/* White connecting lines */}
          <line x1="0" y1="0" x2="50" y2="25" stroke="#fff" strokeWidth="2"/>
          <line x1="50" y1="25" x2="100" y2="0" stroke="#fff" strokeWidth="2"/>
          {/* 2 waves UP-RIGHT from right node */}
          <path d="M104,-2 Q115,-14 125,-4 Q135,-16 148,-6" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Cluster 3 — Bottom center, horizontal chain */}
      <div className="mol-bloom-d2" style={{position:'absolute',bottom:55,left:'50%',transform:'translateX(-50%)',pointerEvents:'none'}}>
        <svg width="160" height="50" viewBox="-55 -20 230 55">
          {/* Nodes: straight horizontal line */}
          <circle cx="0" cy="0" r="4" fill="#fff"/>
          <circle cx="55" cy="0" r="3" fill="#fff"/>
          <circle cx="110" cy="0" r="4" fill="#fff"/>
          {/* White connecting lines */}
          <line x1="0" y1="0" x2="55" y2="0" stroke="#fff" strokeWidth="2"/>
          <line x1="55" y1="0" x2="110" y2="0" stroke="#fff" strokeWidth="2"/>
          {/* 2 waves LEFT from left node */}
          <path d="M-4,0 Q-18,-10 -28,0 Q-38,10 -50,0" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
          {/* 2 waves RIGHT from right node */}
          <path d="M114,0 Q128,10 138,0 Q148,-10 162,0" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 mt-[180px] px-7">
        {/* Back button */}
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[#1A1814] text-sm font-medium mb-6"
          >
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
              <path d="M7 1L1 7l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
        )}

        {/* Live badge */}
        <div className="flex items-center gap-2 mb-5">
          <span className="w-2 h-2 bg-[#1A1814] rounded-full animate-pulse"></span>
          <span className="text-[#1A1814]/70 text-[11px] font-semibold tracking-[2px]">LIVE STUDY</span>
        </div>

        {/* Headers */}
        <h1 className="text-[#1A1814] text-[32px] font-semibold leading-tight anim-stagger-1">
          {projectName}
        </h1>
        <p className="text-[#1A1814]/60 text-sm mt-3 leading-relaxed anim-stagger-1">
          by {organiserName} · Dekko Research
        </p>

        {/* Stats cards */}
        <div className="mt-8 flex gap-3 anim-stagger-2">
          {/* Task count */}
          <div className="flex-1 bg-white/50 rounded-2xl p-[18px]">
            <div className="w-10 h-10 bg-[#D4715A] rounded-xl flex items-center justify-center mb-3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="#fff" strokeWidth="2"/>
                <path d="M8 12l3 3 5-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="text-[#1A1814] text-[28px] font-bold">{taskCount}</div>
            <div className="text-[#1A1814]/55 text-[13px] mt-0.5">tasks to do</div>
          </div>

          {/* Estimated time */}
          <div className="flex-1 bg-white/50 rounded-2xl p-[18px]">
            <div className="w-10 h-10 bg-[#7A93B5] rounded-xl flex items-center justify-center mb-3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2"/>
                <path d="M12 7v5l3 3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="text-[#1A1814] text-[28px] font-bold">~45</div>
            <div className="text-[#1A1814]/55 text-[13px] mt-0.5">mins total</div>
          </div>
        </div>

        {/* Task type pills */}
        <div className="mt-4 flex flex-wrap gap-2">
          {['Audio', 'Photo', 'Video', 'Text'].map(type => (
            <span
              key={type}
              className="bg-white/60 text-[#1A1814] px-3.5 py-2 rounded-[20px] text-[13px] font-medium"
            >
              {type}
            </span>
          ))}
        </div>

        {/* Privacy card */}
        <div className="mt-5 bg-white/50 rounded-2xl p-4 flex items-center gap-3.5">
          <div className="w-10 h-10 bg-white/40 rounded-xl flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="5" y="11" width="14" height="10" rx="2" stroke="#1A1814" strokeWidth="2"/>
              <path d="M8 11V7a4 4 0 118 0v4" stroke="#1A1814" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <div className="text-[#1A1814] text-[15px] font-semibold">Private by default</div>
            <div className="text-[#1A1814]/50 text-[13px] mt-0.5">Only researchers see your responses</div>
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={onContinue}
          className="w-full mt-7 bg-[#1A1814] text-white rounded-[28px] py-[18px] text-[15px] font-semibold tracking-[1px] flex items-center justify-center gap-2.5 anim-stagger-3"
        >
          JOIN STUDY
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M5 12h14M13 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Decline option */}
        {onDecline && (
          <p className="text-center mt-4">
            <button onClick={onDecline} className="text-[#1A1814]/50 text-sm underline">
              I don't want to participate
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default JoinScreen;
